import type { JSX } from 'preact'

/**
 * Code-drawn hub icons (placeholders for art A-2, A-3 and A-22): simple 24×24 shapes in
 * `currentColor`, so the CSS colours them and nothing is downloaded.
 */
export type IconName =
  | 'tasks'
  | 'profile'
  | 'shop'
  | 'settings'
  | 'hat'
  | 'face'
  | 'top'
  | 'bottoms'
  | 'shoes'
  | 'back'
  | 'runners'
  | 'rides'
  | 'close'
  | 'go'

const PATHS: Record<IconName, JSX.Element> = {
  tasks: (
    <>
      <circle cx="12" cy="12" r="8.5" fill="none" stroke="currentColor" stroke-width="2.5" />
      <path
        d="M8 12.5l2.8 2.8L16.5 9"
        fill="none"
        stroke="currentColor"
        stroke-width="2.8"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </>
  ),
  profile: (
    <>
      <circle cx="12" cy="9" r="5" fill="currentColor" />
      <path d="M3.5 21c1.2-4.4 4.6-6.5 8.5-6.5s7.3 2.1 8.5 6.5z" fill="currentColor" />
    </>
  ),
  shop: (
    <>
      <path d="M4 9h16l-1.6 11H5.6z" fill="currentColor" />
      <path d="M8.5 9V7a3.5 3.5 0 0 1 7 0v2" fill="none" stroke="currentColor" stroke-width="2.4" />
    </>
  ),
  settings: (
    <>
      <path
        d="M12 2.5l2 2.6 3.2-.8.6 3.3 3 1.5-1.5 3 1.5 2.9-3 1.5-.6 3.3-3.2-.8-2 2.5-2-2.5-3.2.8-.6-3.3-3-1.5 1.5-2.9-1.5-3 3-1.5.6-3.3 3.2.8z"
        fill="currentColor"
      />
      <circle cx="12" cy="12" r="3.4" fill="var(--icon-hole, #17283a)" />
    </>
  ),
  hat: <path d="M4 17c0-6 3.6-10 8-10s8 4 8 10zM2 17h20v3H2z" fill="currentColor" />,
  face: (
    <>
      <circle cx="7" cy="13" r="4" fill="currentColor" />
      <circle cx="17" cy="13" r="4" fill="currentColor" />
      <path d="M10.5 12.5h3" stroke="currentColor" stroke-width="2.4" />
    </>
  ),
  top: <path d="M8 3l4 2.5L16 3l6 4-2.6 4.2L17 10v11H7V10l-2.4 1.2L2 7z" fill="currentColor" />,
  bottoms: <path d="M5 3h14l1.5 18h-6.5L12 11l-2 10H3.5z" fill="currentColor" />,
  shoes: <path d="M3 9h6l2 3 7 2c2.4.7 3.5 2.3 3.5 4.5V20H3z" fill="currentColor" />,
  back: <path d="M7 3h10l4 18c-3 1-6-1-9 0s-6 1-9 0z" fill="currentColor" />,
  runners: (
    <>
      <circle cx="8" cy="9" r="4.5" fill="currentColor" />
      <circle cx="16.5" cy="10.5" r="4" fill="currentColor" opacity="0.7" />
      <path d="M1.5 21c.8-3.8 3.4-6 6.5-6s5.7 2.2 6.5 6z" fill="currentColor" />
      <path
        d="M15 21c-.2-2-1-3.6-2-4.6 1-.6 2.2-1 3.5-1 2.8 0 5 2 5.8 5.6z"
        fill="currentColor"
        opacity="0.7"
      />
    </>
  ),
  rides: (
    <path
      d="M3 15c0-4 3.6-7 8-7 3.2 0 5.6 1.4 7 3.5l3-1.5-1 4.5c0 2.5-2 4.5-4.5 4.5H6.5A3.5 3.5 0 0 1 3 15zM8 19.5V22M15 19.5V22"
      fill="currentColor"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
    />
  ),
  close: (
    <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="3" stroke-linecap="round" />
  ),
  go: (
    <path
      d="M5 6l6 6-6 6M12 6l6 6-6 6"
      fill="none"
      stroke="currentColor"
      stroke-width="3"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  ),
}

export function Icon({ name, class: cls }: { name: IconName; class?: string | undefined }) {
  return (
    <svg
      class={cls}
      viewBox="0 0 24 24"
      width="24"
      height="24"
      aria-hidden="true"
      focusable="false"
    >
      {PATHS[name]}
    </svg>
  )
}
